import { ReactNode } from 'react';

import { VisitTimeSinceLast } from '@dreryk/egabinet-visit-frontend-api';
import { VisitId } from '@dreryk/egabinet-visit-published-language-api';

import {
   createSingleInterpolatedTranslationKey,
   TranslatableText,
   useTranslate,
   useTranslateDeclension,
} from '@dreryk/react-common';
import { Text } from '@dreryk/react-components';

import { translationKeys } from '../../translations';

import { TimeSinceLastVisitValue } from './TimeSinceLastVisitValue';

const t = translationKeys.timeSinceLastVisit;

type TimeSinceLastVisitProps = {
   readonly timeSinceLast: VisitTimeSinceLast;
   readonly getVisitPath?: (visitId: VisitId) => string;
   readonly backActionLabel?: TranslatableText;
};

export const TimeSinceLastVisit = (props: TimeSinceLastVisitProps): JSX.Element => {
   const translate = useTranslate();
   const {
      daysSinceLastDoctorVisit,
      daysSinceLastSamePractitionerVisit,
      lastDoctorVisitId,
      lastSamePractitionerVisitId,
   } = props.timeSinceLast;

   const doctorVisitHasData = daysSinceLastDoctorVisit !== undefined;
   const samePractitionerVisitHasData = daysSinceLastSamePractitionerVisit !== undefined;

   const doctorVisitDaysCount = daysSinceLastDoctorVisit ?? 0;
   const samePractitionerVisitDaysCount = daysSinceLastSamePractitionerVisit ?? 0;

   const doctorVisitDays = useTranslateDeclension(
      doctorVisitDaysCount,
      createSingleInterpolatedTranslationKey(t.dayCount.accusative, 'count', doctorVisitDaysCount),
      createSingleInterpolatedTranslationKey(t.dayCount.genitive, 'count', doctorVisitDaysCount),
      createSingleInterpolatedTranslationKey(t.dayCount.plural, 'count', doctorVisitDaysCount)
   );
   const samePractitionerVisitDays = useTranslateDeclension(
      samePractitionerVisitDaysCount,
      createSingleInterpolatedTranslationKey(t.dayCount.accusative, 'count', samePractitionerVisitDaysCount),
      createSingleInterpolatedTranslationKey(t.dayCount.genitive, 'count', samePractitionerVisitDaysCount),
      createSingleInterpolatedTranslationKey(t.dayCount.plural, 'count', samePractitionerVisitDaysCount)
   );

   const resolveVisitPath = (visitId?: VisitId): string | undefined =>
      visitId === undefined ? undefined : props.getVisitPath?.(visitId);

   const resolveValue = (hasData: boolean, daysCount: number, days: string, visitId?: VisitId): ReactNode => {
      if (!hasData) {
         return translate(t.noVisit);
      }

      const label =
         daysCount === 0
            ? translate(t.today)
            : translate(createSingleInterpolatedTranslationKey(t.daysAgo, 'days', days));

      return (
         <TimeSinceLastVisitValue
            label={label}
            visitPath={resolveVisitPath(visitId)}
            backActionLabel={props.backActionLabel}
         />
      );
   };

   const doctorVisitValue = resolveValue(doctorVisitHasData, doctorVisitDaysCount, doctorVisitDays, lastDoctorVisitId);
   const samePractitionerVisitValue = resolveValue(
      samePractitionerVisitHasData,
      samePractitionerVisitDaysCount,
      samePractitionerVisitDays,
      lastSamePractitionerVisitId
   );

   return (
      <>
         <Text testId={'lastSamePractitionerVisit'}>
            {translate(createSingleInterpolatedTranslationKey(t.lastSamePractitionerVisit, 'value', ''))}
            {samePractitionerVisitValue}
         </Text>
         <Text testId={'lastDoctorVisit'}>
            {translate(createSingleInterpolatedTranslationKey(t.lastDoctorVisit, 'value', ''))}
            {doctorVisitValue}
         </Text>
      </>
   );
};
